import { Component, ChangeDetectionStrategy, input, output, computed } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { DocumentTemplateWithPath } from '../../../../types/document-template';
import { StructureType } from '../../../../types/fields';
import { isTemplateOutOfSync, TemplateAnchorStatus } from '../../../../utils/templates.utils';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-template-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule, MatIconModule, MatMenuModule, MatDividerModule, MatTooltip],
  styles: `
    :host {
      display: block;
    }
  `,
  template: `
    <div class="group relative flex flex-col bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 shadow-sm hover:shadow-md hover:border-blue-300 dark:hover:border-blue-500 transition-all overflow-hidden">
      <!-- Stretched link: makes the whole card open the template details.
           The visible title link below is the accessible name for this navigation. -->
      <a
        data-testid="template-card-stretched-link"
        [routerLink]="['/templates', template().id]"
        class="absolute inset-0 z-0"
        tabindex="-1"
        aria-hidden="true"
      ></a>
      @if (isDeleting() || isBusy()) {
        <div class="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-800/70 backdrop-blur-[1px]" data-testid="template-card-busy-spinner">
          <div class="animate-spin rounded-full h-5 w-5 border-2 border-zinc-300 dark:border-zinc-500"
               [class]="isDeleting() ? 'border-t-red-500' : 'border-t-indigo-500'"></div>
        </div>
      }
      <div class="p-5 flex-1">
        <!-- Header Row -->
        <div class="flex items-start justify-between mb-3">
          <div class="flex gap-2 min-w-0">
            @if (isAutoBlocked()) {
              <div
                data-testid="template-card-blocked-icon"
                class="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-600 text-red-600 dark:text-red-500"
                matTooltip="Template is blocked, any associated document will automatically go into manual review">
                <mat-icon class="!text-[20px] !w-5 !h-5 !leading-5" aria-hidden="true">block</mat-icon>
              </div>
            } @else {
              <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800" [style.color]="structureColor()">
                <mat-icon class="!text-[20px] !w-5 !h-5 !leading-5" aria-hidden="true">description</mat-icon>
              </div>
            }
            <div class="flex flex-col min-w-0">
              <a data-testid="template-card-view-link" [routerLink]="['/templates', template().id]" class="relative z-10 block group/link">
                <h3 class="font-semibold text-zinc-900 dark:text-zinc-100 text-base mb-1 truncate group-hover/link:text-blue-600 dark:group-hover/link:text-blue-400 transition-colors" [title]="template().name">{{ template().name }}</h3>
              </a>
              <div class="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-none text-zinc-400 dark:text-zinc-500" aria-hidden="true">person</mat-icon>
                <span>{{ template().author }}</span>
              </div>
            </div>
          </div>
          <!-- Actions Menu -->
          <button
            data-testid="template-card-menu-btn"
            type="button"
            aria-label="Template actions"
            [matMenuTriggerFor]="actionsMenu"
            class="relative z-10 h-8 w-8 shrink-0 flex items-center justify-center rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-200 shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-600 hover:text-zinc-700 dark:hover:text-zinc-100 cursor-pointer transition-all"
          >
            <mat-icon class="!text-base !w-4 !h-4 !leading-4" aria-hidden="true">more_vert</mat-icon>
          </button>
          <mat-menu #actionsMenu="matMenu">
            <a
              mat-menu-item
              data-testid="template-card-menu-view"
              [routerLink]="['/templates', template().id]"
            >
              <mat-icon>visibility</mat-icon>
              <span>View</span>
            </a>
            <a
              mat-menu-item
              data-testid="template-card-menu-edit"
              [routerLink]="['/edit-template', template().id]"
            >
              <mat-icon>edit</mat-icon>
              <span>Edit</span>
            </a>
            <button
              mat-menu-item
              data-testid="template-card-menu-export"
              (click)="exportClicked.emit(template())"
            >
              <mat-icon>content_copy</mat-icon>
              <span>Export</span>
            </button>
            @if (isAnchorMismatch()) {
              <button
                mat-menu-item
                data-testid="template-card-menu-reextract"
                (click)="reextractClicked.emit(template())"
              >
                <mat-icon>anchor</mat-icon>
                <span>Re-extract anchors</span>
              </button>
            }
            <button
              mat-menu-item
              data-testid="template-card-menu-toggle-blocked"
              (click)="toggleBlockedClicked.emit(template())"
            >
              <mat-icon>{{ isAutoBlocked() ? 'lock_open' : 'block' }}</mat-icon>
              <span>{{ isAutoBlocked() ? 'Unblock' : 'Block' }}</span>
            </button>
            <mat-divider></mat-divider>
            <button
              mat-menu-item
              data-testid="template-card-menu-delete"
              (click)="deleteClicked.emit(template())"
            >
              <mat-icon class="!text-red-600 dark:!text-red-400">delete</mat-icon>
              <span class="text-red-600 dark:text-red-400">Delete</span>
            </button>
          </mat-menu>
        </div>
        <!-- Template Description -->
        <p class="text-sm text-zinc-500 dark:text-zinc-400 line-clamp-2 min-h-[40px]">{{ template().description || 'No description provided.' }}</p>
        <!-- Anchor Chips -->
        @if (anchorChips().length > 0) {
          <div data-testid="template-card-anchors" class="mt-3 flex flex-wrap gap-1.5">
            @for (chip of anchorChips(); track chip.name) {
              <span
                [attr.data-testid]="'template-card-anchor-chip-' + chip.name"
                [matTooltip]="chip.name + ': ' + chip.value"
                class="relative z-10 inline-flex max-w-full items-center gap-1 rounded-full border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 text-[11px] font-medium text-indigo-700 dark:text-indigo-300">
                <mat-icon class="!text-[12px] !w-3 !h-3 !leading-3 shrink-0" aria-hidden="true">push_pin</mat-icon>
                <span class="truncate">{{ chip.value }}</span>
              </span>
            }
          </div>
        }
        <!-- Meta Info -->
        <div class="mt-4 space-y-2">
          <div class="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
            <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-none text-zinc-400 dark:text-zinc-500" aria-hidden="true">insert_drive_file</mat-icon>
            <span>{{ template().contentType }}</span>
          </div>
          <div class="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
            <mat-icon class="!text-[14px] !w-3.5 !h-3.5 !leading-none text-zinc-400 dark:text-zinc-500" aria-hidden="true">calendar_today</mat-icon>
            <span>Updated {{ formatDate(template().updatedAt) }}</span>
          </div>
        </div>
      </div>
      <!-- Card Footer -->
      <div class="bg-zinc-50 dark:bg-zinc-800/50 border-t border-zinc-100 dark:border-zinc-700 px-5 py-3 flex space-x-1 items-center justify-between">
        @if (structureName()) {
          <span class="text-xs font-mono text-zinc-400 dark:text-zinc-500 truncate grow-1 ">FS: {{ structureName() }}</span>
        } @else {
          <span class="text-xs font-mono text-zinc-400 dark:text-zinc-500 truncate grow-1 ">FS: Unknown</span>
        }

        <div class="flex items-center space-x-1">
        @if (isOutOfSync()) {
          <div class="relative z-10 inline-flex items-center gap-1 text-xs font-medium bg-ds-warning/10 px-2 py-0.5 rounded-full border border-ds-warning text-ds-warning grow-0 shrink-0"
               matTooltip="The fields structure changed after this template was created">
            <mat-icon aria-hidden="true" class="!text-[14px] !w-3.5 !h-3.5 !leading-none">sync_problem</mat-icon>
            Out of sync
          </div>
        }
        @if (isAnchorMismatch()) {
          <div data-testid="template-card-anchor-mismatch"
               class="relative z-10 inline-flex items-center gap-1 text-xs font-medium bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-full border border-amber-400 dark:border-amber-600 text-amber-700 dark:text-amber-300 grow-0 shrink-0 cursor-help"
               matTooltip="This template's anchors don't match its leaf's anchor fields — values are missing, unexpected, or both. Re-extract anchors to realign them.">
            <mat-icon aria-hidden="true" class="!text-[14px] !w-3.5 !h-3.5 !leading-none">anchor</mat-icon>
            Anchor mismatch
          </div>
        }
        <div class="text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-full border border-indigo-100 dark:border-indigo-800 shrink-0 grow-0" [style.color]="structureColor()">
          {{ fieldCount() }} fields
        </div>
        </div>
      </div>
    </div>
  `,
})
export class TemplateCardComponent {
  template = input.required<DocumentTemplateWithPath>();
  structure = input<StructureType>();
  /** Anchor status derived by the parent from the anchor tree (see getTemplateAnchorStatus). */
  anchorStatus = input<TemplateAnchorStatus>('ok');
  isDeleting = input<boolean>(false);
  /** Generic saving/updating overlay (assignment, block toggle, …). */
  isBusy = input<boolean>(false);

  deleteClicked = output<DocumentTemplateWithPath>();
  exportClicked = output<DocumentTemplateWithPath>();
  toggleBlockedClicked = output<DocumentTemplateWithPath>();
  reextractClicked = output<DocumentTemplateWithPath>();

  structureColor = computed(() => this.structure()?.color);
  structureName = computed(() => this.structure()?.name);
  fieldCount = computed(() => Object.keys(this.template().structure || {}).length);
  isOutOfSync = computed(() => isTemplateOutOfSync(this.template(), this.structure()));
  isAnchorMismatch = computed(() => this.anchorStatus() === 'anchor-mismatch');
  isUnplaced = computed(() => this.anchorStatus() === 'unplaced');
  /**
   * Chips rendered on the card: one per anchor value present on the template.
   * Values are stringified so primitives render as-is and objects/arrays render as JSON.
   */
  anchorChips = computed<{ name: string; value: string }[]>(() => {
    const anchors = this.template().anchors;
    if (!anchors) {
      return [];
    }
    return Object.entries(anchors)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([name, value]) => ({ name, value: this.formatAnchorValue(value) }));
  });

  private formatAnchorValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  isAutoBlocked = computed(() => this.template().autoBlocked === true);

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }
}
